import { useEffect, useState, useCallback } from "react";
import { ListOrdered, Plus, Loader2, Search, Megaphone, Play, CheckCircle, SkipForward, Trash2, Clock, CalendarPlus, Hourglass, MonitorPlay } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_STYLES = {
    waiting: "bg-amber-50 text-amber-700 border-amber-200",
    called: "bg-blue-50 text-blue-700 border-blue-200",
    serving: "bg-[#FFF4EC] text-[#C2410C] border-orange-200",
    completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    skipped: "bg-slate-100 text-slate-500 border-slate-200",
    cancelled: "bg-slate-100 text-slate-400 border-slate-200",
};

function PatientPicker({ testPrefix, selected, setSelected }) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    useEffect(() => {
        if (!query.trim() || selected) { setResults([]); return; }
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get("/patients", { params: { search: query } });
                setResults(data);
            } catch { setResults([]); }
        }, 250);
        return () => clearTimeout(t);
    }, [query, selected]);
    if (selected) {
        return (
            <div className="flex items-center justify-between rounded-lg border border-orange-200 bg-[#FFF4EC] px-3 py-2.5" data-testid={`${testPrefix}-selected-patient`}>
                <span className="text-sm font-medium text-slate-900">{selected.name} <span className="font-mono2 text-xs text-[#C2410C]">{selected.patient_uid}</span></span>
                <button type="button" data-testid={`${testPrefix}-clear-patient`} onClick={() => setSelected(null)} className="text-xs text-slate-500 hover:text-red-600">Change</button>
            </div>
        );
    }
    return (
        <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
                data-testid={`${testPrefix}-patient-search`}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search patient by name, phone or ID..."
                className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-9 pr-3.5 text-sm shadow-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none placeholder:text-slate-400"
            />
            {results.length > 0 && (
                <div className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-52 overflow-y-auto" data-testid={`${testPrefix}-patient-results`}>
                    {results.map((p) => (
                        <button key={p.id} type="button" data-testid={`${testPrefix}-patient-option-${p.patient_uid}`} onClick={() => { setSelected(p); setResults([]); }} className="w-full text-left px-4 py-2.5 hover:bg-[#FFF4EC] border-b border-slate-100 last:border-0">
                            <span className="text-sm font-medium text-slate-900">{p.name}</span>
                            <span className="ml-2 text-xs text-slate-500 font-mono2">{p.patient_uid}</span>
                            <span className="ml-2 text-xs text-slate-400">{p.phone}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function Queue() {
    const today = new Date().toISOString().slice(0, 10);
    const [tab, setTab] = useState("board");
    const [date, setDate] = useState(today);
    const [tokens, setTokens] = useState(null);
    const [team, setTeam] = useState([]);
    const [waitlist, setWaitlist] = useState(null);
    const [waitStatus, setWaitStatus] = useState("waiting");
    const [showCheckin, setShowCheckin] = useState(false);
    const [showWait, setShowWait] = useState(false);
    const [saving, setSaving] = useState(false);
    const [ciPatient, setCiPatient] = useState(null);
    const [ciForm, setCiForm] = useState({ with_doctor_id: "", reason: "", priority: "normal" });
    const [wlPatient, setWlPatient] = useState(null);
    const [wlForm, setWlForm] = useState({ preferred_date: "", with_doctor_id: "", reason: "", priority: "normal" });
    const [promoting, setPromoting] = useState(null);
    const [promoteForm, setPromoteForm] = useState({ date: today, time: "" });

    const loadQueue = useCallback(async () => {
        try {
            const { data } = await api.get("/queue", { params: { date } });
            setTokens(data);
        } catch (e) { toast.error(fmtErr(e)); }
    }, [date]);

    const loadWaitlist = useCallback(async () => {
        try {
            const { data } = await api.get("/waitlist", { params: { status: waitStatus } });
            setWaitlist(data);
        } catch (e) { toast.error(fmtErr(e)); }
    }, [waitStatus]);

    useEffect(() => { api.get("/team").then(({ data }) => setTeam(data)).catch(() => {}); }, []);
    useEffect(() => { setTokens(null); loadQueue(); }, [loadQueue]);
    useEffect(() => { if (tab === "waitlist") { setWaitlist(null); loadWaitlist(); } }, [tab, loadWaitlist]);
    useEffect(() => {
        if (tab !== "board" || date !== today) return;
        const iv = setInterval(loadQueue, 20000);
        return () => clearInterval(iv);
    }, [tab, date, today, loadQueue]);

    const checkin = async (e) => {
        e.preventDefault();
        if (!ciPatient) { toast.error("Select a patient"); return; }
        setSaving(true);
        try {
            const { data } = await api.post("/queue/checkin", { patient_id: ciPatient.id, ...ciForm });
            toast.success(`Token #${data.token_no} issued for ${ciPatient.name}`);
            setShowCheckin(false);
            setCiPatient(null);
            setCiForm({ with_doctor_id: "", reason: "", priority: "normal" });
            loadQueue();
        } catch (err) { toast.error(fmtErr(err)); } finally { setSaving(false); }
    };

    const setStatus = async (t, status) => {
        try {
            await api.put(`/queue/${t.id}`, { status });
            loadQueue();
        } catch (e) { toast.error(fmtErr(e)); }
    };

    const removeToken = async (t) => {
        if (!window.confirm(`Remove token #${t.token_no} (${t.patient_name})?`)) return;
        try {
            await api.delete(`/queue/${t.id}`);
            toast.success("Token removed");
            loadQueue();
        } catch (e) { toast.error(fmtErr(e)); }
    };

    const addWait = async (e) => {
        e.preventDefault();
        if (!wlPatient) { toast.error("Select a patient"); return; }
        setSaving(true);
        try {
            await api.post("/waitlist", { patient_id: wlPatient.id, ...wlForm });
            toast.success(`${wlPatient.name} added to waitlist`);
            setShowWait(false);
            setWlPatient(null);
            setWlForm({ preferred_date: "", with_doctor_id: "", reason: "", priority: "normal" });
            loadWaitlist();
        } catch (err) { toast.error(fmtErr(err)); } finally { setSaving(false); }
    };

    const promote = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post(`/waitlist/${promoting.id}/promote`, promoteForm);
            toast.success(`Appointment booked for ${promoting.patient_name}`);
            setPromoting(null);
            setPromoteForm({ date: today, time: "" });
            loadWaitlist();
        } catch (err) { toast.error(fmtErr(err)); } finally { setSaving(false); }
    };

    const cancelWait = async (w) => {
        try {
            await api.put(`/waitlist/${w.id}`, { status: "cancelled" });
            loadWaitlist();
        } catch (e) { toast.error(fmtErr(e)); }
    };

    const deleteWait = async (w) => {
        if (!window.confirm(`Remove ${w.patient_name} from waitlist?`)) return;
        try {
            await api.delete(`/waitlist/${w.id}`);
            loadWaitlist();
        } catch (e) { toast.error(fmtErr(e)); }
    };

    const groups = {};
    (tokens || []).forEach((t) => {
        const key = t.with_doctor_id || "";
        if (!groups[key]) groups[key] = { name: t.with_doctor_name || "Unassigned", tokens: [] };
        groups[key].tokens.push(t);
    });

    const TokenCard = ({ t }) => (
        <div data-testid={`token-card-${t.token_no}`} className={`rounded-lg border p-3 ${t.priority === "urgent" ? "border-red-300 bg-red-50/50" : "border-slate-200 bg-white"}`}>
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="font-heading text-lg font-extrabold text-[#C2410C]">#{t.token_no}</span>
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{t.patient_name}</p>
                        <p className="text-[11px] text-slate-500 truncate">{t.reason || "OPD"}{t.priority === "urgent" ? " · URGENT" : ""}</p>
                    </div>
                </div>
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize ${STATUS_STYLES[t.status]}`} data-testid={`token-status-${t.token_no}`}>{t.status}</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
                {t.status === "waiting" && (
                    <Button size="sm" variant="outline" data-testid={`token-call-${t.token_no}`} onClick={() => setStatus(t, "called")} className="h-7 gap-1 text-xs text-blue-700 border-blue-200 hover:bg-blue-50"><Megaphone className="h-3 w-3" /> Call</Button>
                )}
                {t.status === "called" && (
                    <Button size="sm" variant="outline" data-testid={`token-start-${t.token_no}`} onClick={() => setStatus(t, "serving")} className="h-7 gap-1 text-xs text-[#C2410C] border-orange-200 hover:bg-[#FFF4EC]"><Play className="h-3 w-3" /> Start</Button>
                )}
                {(t.status === "serving" || t.status === "called") && (
                    <Button size="sm" variant="outline" data-testid={`token-done-${t.token_no}`} onClick={() => setStatus(t, "completed")} className="h-7 gap-1 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"><CheckCircle className="h-3 w-3" /> Done</Button>
                )}
                {["waiting", "called"].includes(t.status) && (
                    <Button size="sm" variant="outline" data-testid={`token-skip-${t.token_no}`} onClick={() => setStatus(t, "skipped")} className="h-7 gap-1 text-xs text-slate-500"><SkipForward className="h-3 w-3" /> Skip</Button>
                )}
                {t.status === "skipped" && (
                    <Button size="sm" variant="outline" data-testid={`token-recall-${t.token_no}`} onClick={() => setStatus(t, "waiting")} className="h-7 gap-1 text-xs text-amber-700 border-amber-200 hover:bg-amber-50"><Clock className="h-3 w-3" /> Re-queue</Button>
                )}
                <Button size="sm" variant="ghost" data-testid={`token-remove-${t.token_no}`} onClick={() => removeToken(t)} className="h-7 text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
        </div>
    );

    return (
        <div className="space-y-5 animate-fade-up" data-testid="queue-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">OPD Queue</h1>
                    <p className="mt-1 text-base text-slate-500">Front-desk token management and waitlist</p>
                </div>
                <div className="flex gap-2">
                    <Button data-testid="tv-display-btn" variant="outline" onClick={() => window.open("/queue/display", "_blank")} className="gap-2"><MonitorPlay className="h-4 w-4" /> TV Display</Button>
                    <Button data-testid="add-waitlist-btn" variant="outline" onClick={() => setShowWait(true)} className="gap-2"><Hourglass className="h-4 w-4" /> Add to Waitlist</Button>
                    <Button data-testid="checkin-btn" onClick={() => setShowCheckin(true)} className="bg-[#E06D1F] hover:bg-[#B45309] text-white gap-2"><Plus className="h-4 w-4" /> Check-in Patient</Button>
                </div>
            </div>

            <div className="flex gap-1 border-b border-slate-200" data-testid="queue-tabs">
                {[["board", "Queue Board"], ["waitlist", "Waitlist"]].map(([k, l]) => (
                    <button key={k} data-testid={`queue-tab-${k}`} onClick={() => setTab(k)} className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors duration-150 ${tab === k ? "border-[#E06D1F] text-[#C2410C]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
                ))}
            </div>

            {tab === "board" && (
                <div className="space-y-4">
                    <div className="flex items-center gap-3">
                        <input type="date" data-testid="queue-date-filter" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-lg border border-slate-300 bg-white py-2 px-3 text-sm shadow-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none" />
                        {date === today && <span className="text-xs text-slate-400 flex items-center gap-1"><Clock className="h-3 w-3" /> Auto-refreshes every 20s</span>}
                    </div>
                    {!tokens ? (
                        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#E06D1F]" /></div>
                    ) : tokens.length === 0 ? (
                        <div className="rounded-xl border border-slate-200 bg-white p-12 text-center shadow-sm" data-testid="queue-empty">
                            <ListOrdered className="h-8 w-8 text-slate-300 mx-auto" />
                            <p className="mt-3 text-sm text-slate-500">No tokens for {date}. Check in a patient to start the queue.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {Object.entries(groups).map(([key, g]) => {
                                const serving = g.tokens.filter((t) => ["called", "serving"].includes(t.status)).sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""))[0];
                                const waiting = g.tokens.filter((t) => t.status === "waiting");
                                const others = g.tokens.filter((t) => ["completed", "skipped", "cancelled"].includes(t.status));
                                return (
                                    <div key={key || "none"} className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid={`queue-column-${key || "unassigned"}`}>
                                        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                                            <p className="text-sm font-semibold text-slate-900">{g.name}</p>
                                            <span className="text-xs text-slate-500">{waiting.length} waiting · {others.filter((t) => t.status === "completed").length} done</span>
                                        </div>
                                        {serving ? (
                                            <div className="px-4 py-3 bg-[#FFF4EC] border-b border-orange-100" data-testid={`now-serving-${key || "unassigned"}`}>
                                                <p className="text-[10px] font-bold uppercase tracking-wider text-[#C2410C]">Now {serving.status === "called" ? "Calling" : "Serving"}</p>
                                                <p className="font-heading text-xl font-extrabold text-slate-900">#{serving.token_no} — {serving.patient_name}</p>
                                            </div>
                                        ) : (
                                            <div className="px-4 py-3 border-b border-slate-100"><p className="text-xs text-slate-400">No patient being served</p></div>
                                        )}
                                        <div className="p-3 space-y-2 max-h-96 overflow-y-auto">
                                            {waiting.map((t) => <TokenCard key={t.id} t={t} />)}
                                            {g.tokens.filter((t) => ["called", "serving", "skipped"].includes(t.status)).map((t) => <TokenCard key={t.id} t={t} />)}
                                            {others.length > 0 && (
                                                <details className="text-xs text-slate-400">
                                                    <summary className="cursor-pointer hover:text-slate-600" data-testid={`completed-toggle-${key || "unassigned"}`}>{others.length} completed/skipped</summary>
                                                    <div className="mt-2 space-y-2">{others.map((t) => <TokenCard key={t.id} t={t} />)}</div>
                                                </details>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {tab === "waitlist" && (
                <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="waitlist-card">
                    <div className="px-5 py-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
                        <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><Hourglass className="h-4 w-4 text-[#E06D1F]" /> Waitlist</h3>
                        <select data-testid="waitlist-status-filter" value={waitStatus} onChange={(e) => setWaitStatus(e.target.value)} className="rounded-lg border border-slate-300 bg-white py-2 px-3 text-sm shadow-sm focus:border-orange-500 outline-none">
                            <option value="waiting">Waiting</option>
                            <option value="booked">Booked</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="">All</option>
                        </select>
                    </div>
                    {!waitlist ? (
                        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#E06D1F]" /></div>
                    ) : waitlist.length === 0 ? (
                        <p className="px-5 py-10 text-center text-sm text-slate-500" data-testid="waitlist-empty">No waitlist entries.</p>
                    ) : (
                        <div className="divide-y divide-slate-100">
                            {waitlist.map((w) => (
                                <div key={w.id} data-testid={`waitlist-row-${w.id}`} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5">
                                    <div>
                                        <p className="text-sm font-medium text-slate-900">
                                            {w.patient_name} <span className="font-mono2 text-xs text-[#C2410C]">{w.patient_uid}</span>
                                            {w.priority === "urgent" && <span className="ml-2 rounded-full bg-red-50 border border-red-200 px-2 py-0.5 text-[10px] font-semibold text-red-600">URGENT</span>}
                                        </p>
                                        <p className="text-xs text-slate-500">
                                            {w.reason || "Consultation"}{w.with_doctor_name ? ` · ${w.with_doctor_name}` : ""}{w.preferred_date ? ` · Prefers ${w.preferred_date}` : ""} · {w.patient_phone}
                                        </p>
                                    </div>
                                    <div className="flex gap-1.5">
                                        {w.status === "waiting" && (
                                            <Button size="sm" variant="outline" data-testid={`waitlist-promote-${w.id}`} onClick={() => { setPromoting(w); setPromoteForm({ date: w.preferred_date || today, time: "" }); }} className="gap-1.5 text-emerald-700 border-emerald-200 hover:bg-emerald-50"><CalendarPlus className="h-3.5 w-3.5" /> Book Slot</Button>
                                        )}
                                        {w.status === "waiting" && (
                                            <Button size="sm" variant="outline" data-testid={`waitlist-cancel-${w.id}`} onClick={() => cancelWait(w)} className="text-slate-500">Cancel</Button>
                                        )}
                                        <Button size="sm" variant="ghost" data-testid={`waitlist-delete-${w.id}`} onClick={() => deleteWait(w)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <Dialog open={showCheckin} onOpenChange={setShowCheckin}>
                <DialogContent className="max-w-lg" data-testid="checkin-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Check-in Patient</DialogTitle>
                        <DialogDescription>Issue an OPD token. The patient appears on the queue board instantly.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={checkin} className="space-y-4">
                        <div className="space-y-1.5"><Label>Patient *</Label><PatientPicker testPrefix="checkin" selected={ciPatient} setSelected={setCiPatient} /></div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <Label>Doctor</Label>
                                <select data-testid="checkin-doctor-select" value={ciForm.with_doctor_id} onChange={(e) => setCiForm({ ...ciForm, with_doctor_id: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3.5 text-sm shadow-sm focus:border-orange-500 outline-none">
                                    <option value="">Assigned doctor / any</option>
                                    {team.map((d) => <option key={d.id} value={d.id}>{d.name}{d.specialization ? ` — ${d.specialization}` : ""}</option>)}
                                </select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Priority</Label>
                                <select data-testid="checkin-priority-select" value={ciForm.priority} onChange={(e) => setCiForm({ ...ciForm, priority: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3.5 text-sm shadow-sm focus:border-orange-500 outline-none">
                                    <option value="normal">Normal</option>
                                    <option value="urgent">Urgent</option>
                                </select>
                            </div>
                        </div>
                        <div className="space-y-1.5"><Label>Reason</Label><Input data-testid="checkin-reason-input" value={ciForm.reason} onChange={(e) => setCiForm({ ...ciForm, reason: e.target.value })} placeholder="ANC checkup, follow-up, dressing..." /></div>
                        <Button data-testid="checkin-submit-btn" type="submit" disabled={saving || !ciPatient} className="w-full bg-[#E06D1F] hover:bg-[#B45309] text-white">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Issue Token</Button>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={showWait} onOpenChange={setShowWait}>
                <DialogContent className="max-w-lg" data-testid="waitlist-add-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Add to Waitlist</DialogTitle>
                        <DialogDescription>For patients who couldn't get a slot today — book them the moment one opens up.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={addWait} className="space-y-4">
                        <div className="space-y-1.5"><Label>Patient *</Label><PatientPicker testPrefix="waitlist" selected={wlPatient} setSelected={setWlPatient} /></div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5"><Label>Preferred Date</Label><Input data-testid="waitlist-date-input" type="date" value={wlForm.preferred_date} onChange={(e) => setWlForm({ ...wlForm, preferred_date: e.target.value })} /></div>
                            <div className="space-y-1.5">
                                <Label>Doctor</Label>
                                <select data-testid="waitlist-doctor-select" value={wlForm.with_doctor_id} onChange={(e) => setWlForm({ ...wlForm, with_doctor_id: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3.5 text-sm shadow-sm focus:border-orange-500 outline-none">
                                    <option value="">Any doctor</option>
                                    {team.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5"><Label>Reason</Label><Input data-testid="waitlist-reason-input" value={wlForm.reason} onChange={(e) => setWlForm({ ...wlForm, reason: e.target.value })} placeholder="USG review, report discussion..." /></div>
                            <div className="space-y-1.5">
                                <Label>Priority</Label>
                                <select data-testid="waitlist-priority-select" value={wlForm.priority} onChange={(e) => setWlForm({ ...wlForm, priority: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3.5 text-sm shadow-sm focus:border-orange-500 outline-none">
                                    <option value="normal">Normal</option>
                                    <option value="urgent">Urgent</option>
                                </select>
                            </div>
                        </div>
                        <Button data-testid="waitlist-submit-btn" type="submit" disabled={saving || !wlPatient} className="w-full bg-[#E06D1F] hover:bg-[#B45309] text-white">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Add to Waitlist</Button>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={!!promoting} onOpenChange={() => setPromoting(null)}>
                <DialogContent className="max-w-sm" data-testid="promote-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Book Slot — {promoting?.patient_name}</DialogTitle>
                        <DialogDescription>Pick the appointment date and time.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={promote} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5"><Label>Date *</Label><Input data-testid="promote-date-input" type="date" required value={promoteForm.date} onChange={(e) => setPromoteForm({ ...promoteForm, date: e.target.value })} /></div>
                            <div className="space-y-1.5"><Label>Time *</Label><Input data-testid="promote-time-input" type="time" required value={promoteForm.time} onChange={(e) => setPromoteForm({ ...promoteForm, time: e.target.value })} /></div>
                        </div>
                        <Button data-testid="promote-submit-btn" type="submit" disabled={saving} className="w-full bg-[#E06D1F] hover:bg-[#B45309] text-white">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Book Appointment</Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
